import { DateTime } from 'luxon';
import React from 'react';

import Time from 'src/enosikit/components/Time';

/**
 * Generates a date range message specifically for displaying below
 * a BadgeActive (src/components/Badge/BadgeActive/index.jsx) component.
 * This function formats timestamps with React Time components and is designed to be used when
 * showing
 * a time range that appears in conjunction with a BadgeActive component.
 * @param {import('react-intl').IntlShape} intl - The intl object for internationalization.
 * @param {object} active - The active start and finish timestamps.
 * @param {number} active.start - The active start timestamp in seconds since epoch.
 * @param {number} active.finish - The active finish timestamp in seconds since epoch.
 * @returns {React.ReactNode} - The formatted time range message with Time components.
 * @example
 * // Returns React elements with Time components showing ISO formatted dates
 * buildBadgeActiveSummary(intl, { start: 1672498800, finish: 1704034799 });
 *
 * // Returns React elements with Time component for start date
 * buildBadgeActiveSummary(intl, { start: 1672498800, finish: null });
 *
 * // Returns React elements with Time component for finish date
 * buildBadgeActiveSummary(intl, { start: null, finish: 1704034799 });
 *
 * // Returns plain text when no dates are provided
 * buildBadgeActiveSummary(intl, { start: null, finish: null });
 */
export function buildBadgeActiveSummary(intl, active) {
  const { start, finish } = active || {};
  const startOnly = start && !finish;
  const finishOnly = !start && finish;
  const startAndFinish = start && finish;

  if (startOnly) {
    const startDate = (
      <Time
        child={DateTime.fromSeconds(start).toISO()}
        unixTimestamp={start}
      />
    );
    return intl.formatMessage({ id: 'badge.badge_active_time_range.build_badge_active_summary.start_only.text', defaultMessage: '{startDate} to the future' }, { startDate });
  }

  if (finishOnly) {
    const finishDate = (
      <Time
        child={DateTime.fromSeconds(finish).toISO()}
        unixTimestamp={finish}
      />
    );
    return intl.formatMessage({ id: 'badge.badge_active_time_range.build_badge_active_summary.finish_only.text', defaultMessage: 'Active until {finishDate}' }, { finishDate });
  }

  if (startAndFinish) {
    const startDate = (
      <Time
        child={DateTime.fromSeconds(start).toISO()}
        unixTimestamp={start}
      />
    );
    const finishDate = (
      <Time
        child={DateTime.fromSeconds(finish).toISO()}
        unixTimestamp={finish}
      />
    );
    return intl.formatMessage({ id: 'badge.badge_active_time_range.build_badge_active_summary.start_and_finish.text', defaultMessage: '{startDate} to {finishDate}' }, { startDate, finishDate });
  }

  return intl.formatMessage({ id: 'badge.badge_active_time_range.build_badge_active_summary.active_always.text', defaultMessage: 'Active always' });
}

export default buildBadgeActiveSummary;
